import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { IStory, IStoryContent } from '@opengov/stories-shared/types';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import React, { useEffect, useImperativeHandle, useState } from 'react';
import { IUpdateStoryContentProps } from '../../../../../types/storyViewTypes';
import { assertNotNil } from '../../../../../types/typeUtils';
import { CUSTOM_SPREADSHEET_REPORT_MEDIA_TYPE } from '../../../../constants/modals';
import { addMedia, wrapHtmlWithTitleAndSpreadsheetCommentMarkers } from '../../../../utils/dom';
import locale from '../customReports-locale.json';
import { useFetchNamedRangeMarkup } from '../hooks/useFetchNamedRangeMarkup';
import { usePostSpreadsheetTable } from '../hooks/usePostSpreadsheetTable';
import { useFetchNamedRangesForAddReportTable } from './hooks/useFetchNamedRangesForAddReportTable';
import { useFetchSpreadsheetsForAddReportTable } from './hooks/useFetchSpreadsheetsForAddReportTable';
import { styles } from './AddCustomExcelReport.styles';
import { ReportTableHandle } from './AddReportTableModal.types';
import { LoadingSkeleton } from './LoadingSkeleton';
import { publicationCustomReportPath } from '../../../../../routes/paths';

export type CustomExcelReportFormData = {
  spreadsheetId: string;
  namedRangeId: string;
  title: string;
};

type Props = {
  publicationId: string;
  entityId: number;
  entitySubdomain: string;
  story: IStory;
  storyContent: IStoryContent;
  isTemplate: boolean;
  updateStoryContent(storyContent: IStoryContent): void;
  updateStoryContentDB(props: IUpdateStoryContentProps): void;
  onFormDataChange: (isValid: boolean) => void;
};

export const AddCustomExcelReport = React.forwardRef<ReportTableHandle, Props>((props, ref) => {
  const {
    publicationId,
    entityId,
    entitySubdomain,
    story,
    storyContent,
    isTemplate,
    updateStoryContent,
    updateStoryContentDB,
    onFormDataChange
  } = props;
  const [selectedSpreadsheetId, setSelectedSpreadsheetId] = useState('');
  const [selectedNamedRangeId, setSelectedNamedRangeId] = useState('');
  const [titleValue, setTitleValue] = useState('');

  const { data: spreadsheets, isLoading: isLoadingSpreadsheets } = useFetchSpreadsheetsForAddReportTable({
    publicationId
  });

  const {
    data: namedRanges,
    isLoading: rangesQueryLoading,
    isFetching: rangesFetching
  } = useFetchNamedRangesForAddReportTable({ spreadsheetId: selectedSpreadsheetId });

  const isLoadingNamedRanges = rangesQueryLoading && rangesFetching;

  const { refetch: refetchMarkup } = useFetchNamedRangeMarkup({
    publicationId,
    spreadsheetId: selectedSpreadsheetId,
    namedRangeId: selectedNamedRangeId,
    enabled: false // Don't fetch until after POST
  });

  const { mutateAsync: postSpreadsheetTable } = usePostSpreadsheetTable({
    entityId,
    publicationId,
    storyId: story._id,
    storyContentId: storyContent._id
  });

  const isValid = !isEmpty(selectedSpreadsheetId) && !isEmpty(selectedNamedRangeId);

  useEffect(() => {
    onFormDataChange(isValid);
  }, [selectedSpreadsheetId, selectedNamedRangeId, titleValue, isValid, onFormDataChange]);

  const handleSubmit = async () => {
    try {
      // Link spreadsheet to story content
      await postSpreadsheetTable({
        entityId,
        publicationId,
        storyId: story._id,
        storyContentId: storyContent._id,
        spreadsheetId: selectedSpreadsheetId,
        namedRangeId: selectedNamedRangeId,
        title: titleValue
      });

      const { data: markupData } = await refetchMarkup();
      const htmlContent = assertNotNil(markupData?.html);
      const demarcatedHtmlContent = wrapHtmlWithTitleAndSpreadsheetCommentMarkers({
        content: htmlContent,
        title: titleValue
      });
      const newStoryContent = addMedia(
        storyContent,
        demarcatedHtmlContent,
        0,
        CUSTOM_SPREADSHEET_REPORT_MEDIA_TYPE,
        true
      );

      updateStoryContent(newStoryContent);

      updateStoryContentDB({
        story: story,
        isTemplate: isTemplate,
        storyContent: newStoryContent
      });
    } catch (error) {
      throw error; // Re-throw so parent can handle it
    }
  };

  useImperativeHandle(ref, () => ({
    handleSubmit
  }));

  if (isLoadingSpreadsheets) {
    return <LoadingSkeleton />;
  }

  return (
    <Grid container rowSpacing={2} columnSpacing={3}>
      <Grid item xs={12} md={6}>
        <TextField
          variant="outlined"
          value={selectedSpreadsheetId}
          onChange={(e) => {
            setSelectedSpreadsheetId(e.target.value);
            setSelectedNamedRangeId('');
          }}
          select
          label={locale.addReportTableModal.customReport}
          sx={styles.textField}
        >
          {spreadsheets?.spreadsheets?.length ? (
            spreadsheets.spreadsheets.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled value="">
              {locale.addReportTableModal.noCustomReportsAvailable}
            </MenuItem>
          )}
        </TextField>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          variant="outlined"
          value={selectedNamedRangeId}
          onChange={(e) => setSelectedNamedRangeId(e.target.value)}
          select
          label={locale.addReportTableModal.namedRange}
          sx={styles.textField}
          disabled={!selectedSpreadsheetId || isLoadingNamedRanges}
          InputProps={{
            startAdornment: isLoadingNamedRanges ? (
              <InputAdornment position="start">
                <CircularProgress size={16} />
              </InputAdornment>
            ) : undefined
          }}
        >
          {namedRanges?.spreadsheetNamedRanges?.length ? (
            namedRanges.spreadsheetNamedRanges.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled value="">
              {!selectedSpreadsheetId
                ? locale.addReportTableModal.selectCustomReportFirst
                : locale.addReportTableModal.noNamedRangesAvailable}
            </MenuItem>
          )}
        </TextField>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="body1" sx={styles.infoText}>
          These can be created and managed in the{' '}
          <Link href={publicationCustomReportPath(entitySubdomain, publicationId)} sx={styles.customReportsLink}>
            {locale.addReportTableModal.customReportsLink}
          </Link>{' '}
          section of the Publication.
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <TextField
          value={titleValue}
          onChange={(e) => setTitleValue(e.target.value)}
          label={locale.addReportTableModal.titlePlaceholder}
          sx={styles.titleTextField}
          inputProps={{ className: 'no-format' }} //Prevent ovid from overriding
        />
      </Grid>

      <Grid item xs={4}>
        <TextField
          value={'refresh-on-upload'}
          onChange={noop}
          label={locale.addReportTableModal.dataUpdateFrequency}
          disabled={true}
          sx={styles.textField}
          select
        >
          <MenuItem key={1} value="refresh-on-upload">
            {locale.addReportTableModal.refreshOnUpload}
          </MenuItem>
        </TextField>
      </Grid>
    </Grid>
  );
});

AddCustomExcelReport.displayName = 'AddCustomExcelReport';
